import { range } from "remeda";

import { LeftRight } from "@/components/LeftRight.tsx";
import { SimpleMonthlyCostElement } from "@/components/forms/MonthlyCostField.tsx";
import { type ConfiguredWSelectProps, WSelect } from "@/components/forms/WSelect.tsx";
import { WSelectWrapper } from "@/components/forms/WSelectWrapper.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { sizeToVmPriceFields, type SizeValue } from "@/modules/compute/vmSizeSelectUtils.ts";
import { pricesAtom } from "@/modules/pricing/pricingQuery.ts";
import { getAppendedServiceFailoverPrice, type Price } from "@/modules/pricing/resourcePricing.ts";
import { availableServiceImagesAtom } from "@/modules/services/serviceImagesQuery.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Plural, Trans } from "@lingui/react/macro";

interface NodeWithPrice {
    node_count: number;
    price: Price;
    is_default?: boolean;
}

export interface FailoverNodeSelectCustom extends ConfiguredWSelectProps<NodeWithPrice, string> {
    size: SizeValue;
    location: string;
    serviceName: string;
}

export const DefaultNodeCount = 1;

function FailoverNodeIcon() {
    return <MaskIcon className="size-1.125rem jp-stack-icon text-primary" />;
}

function ShortInfo({ item: { price } }: { item: NodeWithPrice }) {
    return <SimpleMonthlyCostElement price={price} show_zero_as="NUMBER" />;
}

function getDefaultNode(items: readonly NodeWithPrice[]) {
    return items.find((s) => s.is_default) ?? items[0];
}

function getItemName(item: NodeWithPrice) {
    return item.node_count === 0 ? (
        <Trans comment="Fail-over is disabled, no fail-over nodes">No Fail-over</Trans>
    ) : (
        <Trans>
            {item.node_count} <Plural value={item.node_count} one="node" other="nodes" />
        </Trans>
    );
}

function FailoverNodeSelectCustom(props: FailoverNodeSelectCustom) {
    return (
        <WSelect
            aria-label="Select failover node"
            dynamicDefault={getDefaultNode}
            getKey={(item) => String(item.node_count)}
            {...props}
        >
            {(item) => (
                <LeftRight icon={<FailoverNodeIcon />} title={getItemName(item)} right={<ShortInfo item={item} />} />
            )}
        </WSelect>
    );
}

export type FailoverNodeSelectLoaderProps = Omit<FailoverNodeSelectCustom, "items">;

function FailoverNodeSelectLoader(props: FailoverNodeSelectLoaderProps) {
    const priceList = useSuspenseQueryAtom(pricesAtom);

    const allImages = useSuspenseQueryAtom(availableServiceImagesAtom);

    const imagePrices = allImages.find((i) => i.service_name === props.serviceName)!.prices;

    const items = range(0, 2).map((i): NodeWithPrice => {
        const price = getAppendedServiceFailoverPrice(
            priceList,
            {
                ...sizeToVmPriceFields(props.size, true),
                location: props.location,
                price_multipliers: imagePrices,
                os_name: props.serviceName,
            },
            false,
            i,
        );
        return {
            node_count: i,
            price,
            is_default: i === DefaultNodeCount,
        };
    });

    return <FailoverNodeSelectCustom {...props} items={items} />;
}

export function FailoverNodeSelect(props: FailoverNodeSelectLoaderProps) {
    return (
        <WSelectWrapper fallback={(renderProps) => <FailoverNodeSelectCustom {...props} {...renderProps} />}>
            <FailoverNodeSelectLoader {...props} />
        </WSelectWrapper>
    );
}
